import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api";

const CatalogContext = createContext({ heroes: [], maps: [], teams: [], defaultTeamId: null });

/** Heroes, maps, teams and the default team: loaded once, refreshed after edits.
 *  The default team only pre-selects team pickers and filters and is listed first. */
export function CatalogProvider({ children }) {
  const [catalog, setCatalog] = useState({ heroes: [], maps: [], map_types: [], modes: [] });
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState("");
  const [defaultTeamId, setDefaultTeamId] = useState(null);

  const refreshTeams = useCallback(async () => {
    setTeamsLoading(true);
    setTeamsError("");
    try {
      const rows = await api.teams();
      if (!Array.isArray(rows)) throw new Error("The teams API returned an invalid response.");
      const normalized = rows.map((team) => ({
        ...team,
        roster: Array.isArray(team?.roster) ? team.roster : [],
        maps: Number(team?.maps || 0),
        wins: Number(team?.wins || 0),
        losses: Number(team?.losses || 0),
        win_rate: Number(team?.win_rate || 0),
      }));
      setTeams(normalized);
      return normalized;
    } catch (error) {
      setTeamsError(error?.message || "Could not load teams.");
      return null;
    } finally {
      setTeamsLoading(false);
    }
  }, []);
  const refreshDefault = () => api.defaultTeam().then((r) => setDefaultTeamId(r.team_id)).catch(() => {});

  useEffect(() => {
    api.catalog().then(setCatalog).catch(() => {});
    refreshTeams();
    refreshDefault();
  }, [refreshTeams]);

  const value = useMemo(() => {
    const byName = Object.fromEntries(catalog.heroes.map((h) => [h.name.toLowerCase(), h]));
    const byId = Object.fromEntries(catalog.heroes.map((h) => [h.id, h]));
    const known = teams.some((t) => t.id === defaultTeamId) ? defaultTeamId : null;
    return {
      ...catalog,
      teams: known ? [...teams.filter((t) => t.id === known), ...teams.filter((t) => t.id !== known)] : teams,
      teamsLoading,
      teamsError,
      defaultTeamId: known,
      hero: (name) => byName[String(name || "").toLowerCase()],
      heroName: (id) => byId[id]?.name || (id ? `#${id}` : ""),
      refreshTeams,
      setDefaultTeamId,
    };
  }, [catalog, teams, teamsLoading, teamsError, defaultTeamId, refreshTeams]);
  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export const useCatalog = () => useContext(CatalogContext);
